import { mat4, vec4 } from 'gl-matrix';
import { Light } from './Light';
import { DirectionalLight } from './DirectionalLight';
import { Camera } from '../camera';
import { ViewPort } from '../context/ViewPort';
import { nextPow2 } from '../math';

const MAX_LIGHTS_PER_TILE = 128;
/** Each tile row: 1 count + MAX_LIGHTS_PER_TILE indices */
const TILE_STRIDE = 1 + MAX_LIGHTS_PER_TILE;

const tempClip = vec4.create();
const tempVP = mat4.create();

const INITIAL_LIGHT_CAPACITY = 256;

/**
 * Choose the smallest power-of-2 tile size that keeps the total tile count
 * under a target ceiling.  Fewer tiles = cheaper CPU assignment; smaller
 * tiles = tighter GPU culling.  The target is a balance between the two.
 */
const TARGET_MAX_TILES = 1024;
const MIN_TILE_SIZE = 16;
const MAX_TILE_SIZE = 128;

function chooseTileSize(viewWidth: number, viewHeight: number): number {
  let size = MIN_TILE_SIZE;
  while (
    size < MAX_TILE_SIZE &&
    Math.ceil(viewWidth / size) * Math.ceil(viewHeight / size) > TARGET_MAX_TILES
  ) {
    size *= 2;
  }
  return size;
}

export class TileLightAssigner {
  public tilesX = 0;
  public tilesY = 0;
  public numTiles = 0;
  public tileSize = 16;
  public tileData!: Float32Array;

  private capacity = 0;

  // Pre-allocated scratch buffers for light partitioning (SoA layout).
  // Grow lazily if a scene exceeds the initial capacity.
  private lightCapacity = INITIAL_LIGHT_CAPACITY;
  private globalIndicesBuf = new Uint16Array(INITIAL_LIGHT_CAPACITY);
  private localLi = new Uint16Array(INITIAL_LIGHT_CAPACITY);
  private localMinTX = new Uint16Array(INITIAL_LIGHT_CAPACITY);
  private localMaxTX = new Uint16Array(INITIAL_LIGHT_CAPACITY);
  private localMinTY = new Uint16Array(INITIAL_LIGHT_CAPACITY);
  private localMaxTY = new Uint16Array(INITIAL_LIGHT_CAPACITY);

  private ensureLightCapacity(needed: number): void {
    if (needed <= this.lightCapacity) return;
    this.lightCapacity = nextPow2(needed);
    this.globalIndicesBuf = new Uint16Array(this.lightCapacity);
    this.localLi = new Uint16Array(this.lightCapacity);
    this.localMinTX = new Uint16Array(this.lightCapacity);
    this.localMaxTX = new Uint16Array(this.lightCapacity);
    this.localMinTY = new Uint16Array(this.lightCapacity);
    this.localMaxTY = new Uint16Array(this.lightCapacity);
  }

  assign(camera: Camera, lights: Light[], viewPort: ViewPort): void {
    const tileSize = chooseTileSize(viewPort.width, viewPort.height);
    this.tileSize = tileSize;
    this.tilesX = Math.ceil(viewPort.width / tileSize);
    this.tilesY = Math.ceil(viewPort.height / tileSize);
    this.numTiles = this.tilesX * this.tilesY;

    // Grow tile buffer if needed
    if (this.numTiles > this.capacity) {
      this.capacity = nextPow2(this.numTiles);
      this.tileData = new Float32Array(this.capacity * TILE_STRIDE);
    }

    const td = this.tileData;

    if (lights.length === 0) {
      for (let t = 0; t < this.numTiles; t++) td[t * TILE_STRIDE] = 0;
      return;
    }

    this.ensureLightCapacity(lights.length);

    mat4.multiply(tempVP, camera.projectionMatrix, camera.modelViewMatrix);

    const width = viewPort.width;
    const height = viewPort.height;
    const projScaleX = Math.abs(camera.projectionMatrix[0]) * 0.5 * width;
    const projScaleY = Math.abs(camera.projectionMatrix[5]) * 0.5 * height;
    const lastTileX = this.tilesX - 1;
    const lastTileY = this.tilesY - 1;
    const numTiles = this.numTiles;
    const tilesX = this.tilesX;

    // Lights covering more than half the tile grid are "global" — assigned to
    // every tile in bulk rather than iterating their per-tile AABB.
    const globalThreshold = numTiles >> 1;

    // Phase 1: project lights, partition into global (cover most/all tiles)
    // and local (cover specific tiles). Uses pre-allocated SoA buffers.
    let globalCount = 0;
    let localCount = 0;
    const globalIndices = this.globalIndicesBuf;
    const lLi = this.localLi;
    const lMinTX = this.localMinTX;
    const lMaxTX = this.localMaxTX;
    const lMinTY = this.localMinTY;
    const lMaxTY = this.localMaxTY;

    for (let li = 0; li < lights.length; li++) {
      const light = lights[li];

      if (light instanceof DirectionalLight) {
        globalIndices[globalCount++] = li;
        continue;
      }

      const pos = light.position;
      vec4.set(tempClip, pos[0] as number, pos[1] as number, pos[2] as number, 1);
      vec4.transformMat4(tempClip, tempClip, tempVP);
      const w = tempClip[3];

      if (w <= 0) {
        globalIndices[globalCount++] = li;
        continue;
      }

      const screenX = ((tempClip[0] / w) * 0.5 + 0.5) * width;
      const screenY = ((tempClip[1] / w) * 0.5 + 0.5) * height;
      const outerRange = light.boundingSphere!.radius;
      const srx = (outerRange * projScaleX) / w;
      const sry = (outerRange * projScaleY) / w;

      const minTX = Math.max(0, Math.floor((screenX - srx) / tileSize) - 1);
      const maxTX = Math.min(lastTileX, Math.floor((screenX + srx) / tileSize) + 1);
      const minTY = Math.max(0, Math.floor((screenY - sry) / tileSize) - 1);
      const maxTY = Math.min(lastTileY, Math.floor((screenY + sry) / tileSize) + 1);

      if (minTX > maxTX || minTY > maxTY) continue;

      const tilesCovered = (maxTX - minTX + 1) * (maxTY - minTY + 1);
      if (tilesCovered > globalThreshold) {
        globalIndices[globalCount++] = li;
      } else {
        lLi[localCount] = li;
        lMinTX[localCount] = minTX;
        lMaxTX[localCount] = maxTX;
        lMinTY[localCount] = minTY;
        lMaxTY[localCount] = maxTY;
        localCount++;
      }
    }

    // Phase 2: if global lights alone exceed the cap, every tile overflows.
    if (globalCount > MAX_LIGHTS_PER_TILE) {
      for (let t = 0; t < numTiles; t++) td[t * TILE_STRIDE] = MAX_LIGHTS_PER_TILE;
      return;
    }

    // Phase 3: seed every tile with the global lights in one pass.
    for (let t = 0; t < numTiles; t++) {
      const base = t * TILE_STRIDE;
      td[base] = globalCount;
      for (let g = 0; g < globalCount; g++) {
        td[base + 1 + g] = globalIndices[g];
      }
    }

    // Phase 4: assign local lights to their specific tiles.
    for (let l = 0; l < localCount; l++) {
      const li = lLi[l];
      const minTX = lMinTX[l];
      const maxTX = lMaxTX[l];
      const minTY = lMinTY[l];
      const maxTY = lMaxTY[l];

      for (let ty = minTY; ty <= maxTY; ty++) {
        const rowOffset = ty * tilesX;
        for (let tx = minTX; tx <= maxTX; tx++) {
          const base = (rowOffset + tx) * TILE_STRIDE;
          const count = td[base];
          if (count >= MAX_LIGHTS_PER_TILE) continue;
          td[base + 1 + count] = li;
          td[base] = count + 1;
        }
      }
    }
  }
}
